<template>
    <div>
        <div v-show="element.location.type === 'link'">
            <!-- YouTube videos -->
            <div
                v-if="reTestYouTube.test(element.location.url)"
                data-test-id="content-link-youtube"
            >
                <iframe
                    :src="
                        `https://www.youtube.com/embed/${
                            element.location.url.match(reMatchYouTube)[1]
                        }?rel=0&amp;showinfo=0`
                    "
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                ></iframe>
            </div>
            <!--/YouTube videos -->

            <!-- Loom videos -->
            <div
                v-else-if="element.location.url.includes('loom.com/embed')"
                data-test-id="content-link-loom"
            >
                <iframe
                    :src="element.location.url"
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                ></iframe>
            </div>
            <!--/Loom videos -->

            <!-- Google Docs -->
            <div
                v-else-if="reTestGoogleDocsPub.test(element.location.url)"
                data-test-id="content-link-google-docs"
            >
                <iframe
                    :src="
                        `${element.location.url
                            .replace('/pub?start=', '/embed?start=')
                            .replace('/pubhtml', '/pubhtml?widget=true&amp;headers=false')
                            .replace('/pub', '/pub?embedded=true')}`
                    "
                    frameborder="0"
                    allowfullscreen="true"
                    mozallowfullscreen="true"
                    webkitallowfullscreen="true"
                ></iframe>
            </div>
            <!--/Google Docs -->

            <!-- Other Links (Open Graph Preview) -->
            <div v-else>
                <div
                    v-if="element.urlMetadata && element.urlMetadata.openGraphData"
                    data-test-id="content-link-ogp"
                >
                    <l-link-preview
                        :title="element.urlMetadata.openGraphData.ogTitle"
                        :description="element.urlMetadata.openGraphData.ogDescription"
                        :image="element.urlMetadata.openGraphData.ogImage.url"
                        :url="element.location.url"
                    />
                </div>
            </div>
            <!--/Other Links (Open Graph Preview) -->

            <br />

            <!-- Open Link in New Window -->
            <div class="text-center" data-test-id="content-link-open">
                <a
                    class="btn btn-primary btn-framed-primary"
                    :href="element.location.url"
                    target="_blank"
                    rel="noopener nofollow"
                >
                    <i class="fa fa-external-link"></i>
                    {{ $t(`learning.admin.element.details.btn.openNewWindow`) }}
                </a>
            </div>
            <!--/Open Link in New Window -->
        </div>
        <div v-show="element.location.type === 'awsupload'">
            <!-- Error -->
            <div v-if="error">
                <l-text-alert type="danger" :text="error.message" />
            </div>
            <!--/Error -->

            <!-- Video -->
            <div
                v-else-if="element.mimetype && element.mimetype.startsWith('video/')"
                data-test-id="content-file-video"
            >
                <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
                <video :src="url" controls></video>
            </div>
            <!--/Video -->

            <!-- Docs + Presentations -->
            <div
                v-else-if="
                    element.mimetype &&
                        (element.mimetype.endsWith('presentation') ||
                            element.mimetype.endsWith('document'))
                "
                data-test-id="content-file-doc"
            >
                <iframe
                    :src="
                        `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                            url,
                        )}`
                    "
                    frameborder="0"
                ></iframe>
            </div>
            <!--/Docs + Presentations -->

            <!-- Images -->
            <div
                v-else-if="element.mimetype && element.mimetype.startsWith('image/')"
                data-test-id="content-file-image"
            >
                <img v-show="url" :src="url" :alt="element.name" />
            </div>
            <!--/Images -->

            <!-- Others -->
            <div v-else data-test-id="content-file-others">
                <object
                    v-show="url"
                    :data="url"
                    :aria-label="element.name"
                    :type="element.mimetype"
                ></object>
            </div>
            <!--/Others -->

            <br />

            <!-- Download File -->
            <div
                v-if="!error && element.mimetype && !element.mimetype.startsWith('video/')"
                class="text-center"
                data-test-id="content-file-download"
            >
                <button class="btn btn-primary btn-framed-primary" @click="downloadFile">
                    <i class="fa fa-download"></i>
                    {{ $t(`learning.admin.element.details.btn.download`) }}
                </button>
            </div>
            <!--/Download File -->
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LLinkPreview from "@/components/LLinkPreview";
import LTextAlert from "@/components/LTextAlert";

import getFileNameFromAWSKey from "@/utils/get-file-name-from-aws-key";

export default {
    components: {
        LLinkPreview,
        LTextAlert,
    },
    props: {
        element: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            reMatchYouTube: /^.*(?:youtu.be\/|v\/|e\/|u\/\w+\/|embed\/|v=)([^#&?]*).*/,
            reTestYouTube: /^https:\/\/(www\.)?(youtu.be|youtube.com)\/(?!playlist).*/,

            // Supports Docs, Slides and Spreadsheets
            reTestGoogleDocsPub: /^https:\/\/docs.google.com\/.*\/(pub|embed|pubhtml)/,

            error: null,
        };
    },
    computed: {
        ...mapGetters({
            url: "learning/storage/url",
        }),
    },
    created() {
        if (this.element.location?.type === "awsupload") {
            this.element.location.filename = getFileNameFromAWSKey(this.element.location.key);

            // Fetch file for preview
            // Note: the file's URL will be stored in `this.url`
            //
            this.$store
                .dispatch({
                    type: "learning/storage/download",
                    payload: { key: this.element.location.key, mimetype: this.element.mimetype },
                })
                .catch(err => {
                    this.error = { message: err.response.data.message };
                });
        }
    },
    methods: {
        downloadFile() {
            if (this.error) {
                return;
            }

            window.open(this.url, "_blank", "noopener noreferrer");
        },
    },
};
</script>

<style scoped>
object,
iframe {
    width: 100%;
    height: 50vh;
}
img,
video {
    width: 100%;
}

.element-link i {
    margin-right: 4px;
}
</style>
